import { createContext, useContext, useEffect, useLayoutEffect, useState, type ReactNode } from 'react'
import { X } from 'lucide-react'

const ModalFormContext = createContext<{
  setFormState: (state: { saving: boolean; dirty: boolean }) => void
  requestClose: () => void
} | null>(null)

export function useModalFormState(saving: boolean, dirty: boolean) {
  const context = useContext(ModalFormContext)
  const setState = context?.setFormState
  useLayoutEffect(() => { setState?.({ saving, dirty }) }, [setState, saving, dirty])
  useLayoutEffect(() => () => setState?.({ saving: false, dirty: false }), [setState])
  return context?.requestClose
}

export function Modal({ open, onClose, title, description, children, size = 'lg' }: {
  open: boolean; onClose: () => void; title: string; description?: string; children: ReactNode; size?: 'md' | 'lg' | 'xl'
}) {
  const [formState, setFormState] = useState({ saving: false, dirty: false })
  const requestClose = () => {
    if (formState.saving) return
    if (formState.dirty && !window.confirm('Discard your unsaved changes?')) return
    onClose()
  }
  useEffect(() => {
    if (!open) return
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const close = (event: KeyboardEvent) => { if (event.key === 'Escape') requestClose() }
    window.addEventListener('keydown', close)
    return () => { document.body.style.overflow = original; window.removeEventListener('keydown', close) }
  }, [open, onClose, formState.saving, formState.dirty])

  if (!open) return null
  const width = size === 'xl' ? 'max-w-3xl' : size === 'md' ? 'max-w-lg' : 'max-w-2xl'
  return (
    <ModalFormContext.Provider value={{ setFormState, requestClose }}>
    <div className="fixed inset-0 z-[70] overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="modal-title" aria-busy={formState.saving}>
      <button className="fixed inset-0 bg-pine-950/50 backdrop-blur-[2px]" disabled={formState.saving} onClick={requestClose} aria-label="Close dialog" />
      <div className="flex min-h-full items-center justify-center p-4 sm:p-6">
        <div className={`relative w-full ${width} overflow-hidden rounded-2xl bg-white shadow-modal`}>
          <div className="flex items-start justify-between border-b border-slate-100 px-5 py-5 sm:px-6">
            <div className="pr-8">
              <h2 id="modal-title" className="text-lg font-bold tracking-tight text-slate-950">{title}</h2>
              {description && <p className="mt-1 text-sm leading-5 text-slate-500">{description}</p>}
            </div>
            <button disabled={formState.saving} onClick={requestClose} className="absolute right-4 top-4 rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40" aria-label="Close"><X className="h-5 w-5" /></button>
          </div>
          {children}
        </div>
      </div>
    </div>
    </ModalFormContext.Provider>
  )
}
